import type { ChannelClient } from '@hibro/platform-core';
import {
  DEFAULT_LOCALE,
  EN_US_SHELL,
  type INlsService,
  type Locale,
  LOCALE_CHANGED_EVENT,
  type LocaleChange,
  type MessageCatalog,
} from '@hibro/shared';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { createTranslator, TranslatorContext } from './i18n';

export interface I18nProviderProps {
  /** Auto RPC proxy for the host nls service (架构方案 §5.11). */
  nlsService: INlsService;
  /** Channel client, to subscribe to the cross-window locale broadcast. */
  client: ChannelClient;
  children: ReactNode;
}

/**
 * Provides the active translator to a React subtree (见 14 / §5.11).
 * Pulls the catalog + locale on mount and re-renders on `LOCALE_CHANGED_EVENT`
 * (a language switch in any window, or an OS-locale flip while on `system`).
 * Mirrors the theme apply path, but in React because copy lives in components
 * and must re-render on change. The event carries preference + locale only, so
 * this re-pulls the full catalog (the renderer is trusted).
 */
export function I18nProvider({
  nlsService,
  client,
  children,
}: I18nProviderProps): ReactNode {
  const [snapshot, setSnapshot] = useState<{
    locale: Locale;
    catalog: MessageCatalog;
  }>({ locale: DEFAULT_LOCALE, catalog: EN_US_SHELL });

  useEffect(() => {
    let active = true;
    const refresh = (locale?: Locale): void => {
      void Promise.all([
        locale ? Promise.resolve(locale) : nlsService.getLocale(),
        nlsService.getCatalog(),
      ]).then(([resolved, catalog]) => {
        if (active) {
          setSnapshot({ locale: resolved, catalog });
        }
      });
    };
    refresh();
    const dispose = client.listen(LOCALE_CHANGED_EVENT, (data) => {
      refresh((data as LocaleChange).locale);
    });
    return () => {
      active = false;
      dispose();
    };
  }, [nlsService, client]);

  const translator = useMemo(
    () => createTranslator(snapshot.locale, snapshot.catalog),
    [snapshot],
  );

  return (
    <TranslatorContext.Provider value={translator}>
      {children}
    </TranslatorContext.Provider>
  );
}
